import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { VerificationDemoProvider } from "@/context/VerificationDemoContext";
import { roleHome } from "@/lib/format";
import RoleLayout from "@/components/RoleLayout";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import LegalDocPage from "@/pages/legal/LegalDocPage";
import ResetPassword from "@/pages/ResetPassword";
import ProfilePage from "@/pages/ProfilePage";

import BusinessDashboard from "@/pages/business/BusinessDashboard";
import CreateService from "@/pages/business/CreateService";
import BusinessServices from "@/pages/business/BusinessServices";
import BusinessHistory from "@/pages/business/BusinessHistory";
import BusinessAccount from "@/pages/business/BusinessAccount";
import ServiceDetail from "@/pages/business/ServiceDetail";
import BusinessDocuments from "@/pages/business/BusinessDocuments";

import DriverDashboard from "@/pages/driver/DriverDashboard";
import DriverHistory from "@/pages/driver/DriverHistory";
import DriverEarnings from "@/pages/driver/DriverEarnings";
import DriverVehicle from "@/pages/driver/DriverVehicle";
import DriverDocuments from "@/pages/driver/DriverDocuments";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminServices from "@/pages/admin/AdminServices";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminDrivers from "@/pages/admin/AdminDrivers";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminIncidents from "@/pages/admin/AdminIncidents";
import AdminSettlements from "@/pages/admin/AdminSettlements";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminDriverPayouts from "@/pages/admin/AdminDriverPayouts";
import AdminVerification from "@/pages/admin/AdminVerification";
// Pantallas DEMO (estado local en navegador) conservadas bajo rutas "-demo".
import AdminVerificationDrivers from "@/pages/admin/AdminVerificationDrivers";
import AdminVerificationBusinesses from "@/pages/admin/AdminVerificationBusinesses";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminStuckServices from "@/pages/admin/AdminStuckServices";

function RootRedirect() {
  const { loading, session, profile } = useAuth();
  if (loading)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0B0B0B]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#27272A] border-t-[#FF6A00]" />
      </div>
    );
  if (!session) return <Landing />;
  if (!profile)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0B0B0B]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#27272A] border-t-[#FF6A00]" />
      </div>
    );
  return <Navigate to={roleHome(profile.role)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <VerificationDemoProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/welcome" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            {/* Documentos legales del piloto (borradores para pruebas internas) */}
            <Route path="/legal/terminos" element={<LegalDocPage doc="terminos" />} />
            <Route path="/legal/privacidad" element={<LegalDocPage doc="privacidad" />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* BUSINESS */}
            <Route element={<RoleLayout role="BUSINESS" />}>
              <Route path="/business" element={<BusinessDashboard />} />
              <Route path="/business/new" element={<CreateService />} />
              <Route path="/business/services" element={<BusinessServices />} />
              <Route path="/business/history" element={<BusinessHistory />} />
              <Route path="/business/account" element={<BusinessAccount />} />
              <Route path="/business/service/:id" element={<ServiceDetail />} />
              <Route path="/business/documents" element={<BusinessDocuments />} />
              <Route path="/business/profile" element={<ProfilePage />} />
            </Route>

            {/* DRIVER */}
            <Route element={<RoleLayout role="DRIVER" />}>
              <Route path="/driver" element={<DriverDashboard />} />
              <Route path="/driver/history" element={<DriverHistory />} />
              <Route path="/driver/earnings" element={<DriverEarnings />} />
              <Route path="/driver/vehicle" element={<DriverVehicle />} />
              <Route path="/driver/documents" element={<DriverDocuments />} />
              <Route path="/driver/profile" element={<ProfilePage />} />
            </Route>

            {/* ADMIN */}
            <Route element={<RoleLayout role="ADMIN" />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/services" element={<AdminServices />} />
              <Route path="/admin/stuck" element={<AdminStuckServices />} />
              <Route path="/admin/businesses" element={<AdminBusinesses />} />
              <Route path="/admin/drivers" element={<AdminDrivers />} />
              <Route path="/admin/vehicles" element={<AdminVehicles />} />
              <Route path="/admin/incidents" element={<AdminIncidents />} />
              <Route path="/admin/settlements" element={<AdminSettlements />} />
              <Route path="/admin/finance" element={<AdminFinance />} />
              <Route path="/admin/driver-payouts" element={<AdminDriverPayouts />} />
              <Route path="/admin/verification" element={<AdminVerification />} />
              {/* DEMO (datos ficticios en navegador) — separadas del flujo real */}
              <Route path="/admin/verification-demo/drivers" element={<AdminVerificationDrivers />} />
              <Route path="/admin/verification-demo/businesses" element={<AdminVerificationBusinesses />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors theme="dark" />
        </BrowserRouter>
        </VerificationDemoProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
